import { supabase } from "../supabaseClient"
import { getValidSupabaseAccessToken } from "../utils/supabaseSession"

export const BILLING_FORBIDDEN_MESSAGE =
  "You do not have permission to manage billing for this organization."

const FUNCTIONS_URL =
  process.env.REACT_APP_SUPABASE_FUNCTION_URL ||
  process.env.NEXT_PUBLIC_SUPABASE_FUNCTION_URL

export const getStripeFunctionsUrl = () => FUNCTIONS_URL || ""

export async function checkBillingPrerequisites() {
  const functionsUrl = getStripeFunctionsUrl()
  const hasFunctionsUrl = Boolean(functionsUrl)

  if (!hasFunctionsUrl) {
    return {
      functionsUrl,
      hasFunctionsUrl,
      accessToken: null,
      hasAccessToken: false,
      reason: "missing_functions_url",
    }
  }

  const accessToken = await getValidSupabaseAccessToken(supabase)
  const hasAccessToken = Boolean(accessToken)

  return {
    functionsUrl,
    hasFunctionsUrl,
    accessToken: accessToken || null,
    hasAccessToken,
    reason: hasAccessToken ? null : "missing_access_token",
  }
}

export async function initializeStripeCheckout(
  organizationId,
  { purpose, flow } = {},
) {
  if (!organizationId) throw new Error("organizationId is required")
  if (!FUNCTIONS_URL) {
    throw new Error("Supabase function URL not configured")
  }

  const token = await getValidSupabaseAccessToken(supabase)
  if (!token) {
    throw new Error("Missing access token")
  }

  const resolvedPurpose =
    purpose ||
    (flow === "billing_portal" ? "manage" : "subscribe")
  const response = await fetch(`${FUNCTIONS_URL}/create-checkout-session`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      organization_id: organizationId,
      purpose: resolvedPurpose,
    }),
  })

  const data = await response.json().catch(() => ({}))
  if (!response.ok) {
    const errorMessage =
      data?.error || "Failed to create Stripe checkout session"
    const error = new Error(errorMessage)
    Object.assign(error, {
      status: response.status,
      payload: data,
    })
    throw error
  }

  const flowValue = typeof data?.flow === "string" ? data.flow : null
  const normalizedFlow = flowValue || "embedded_checkout"

  const planMetadata =
    data?.plan && typeof data.plan === "object" ? data.plan : undefined
  const subscriptionId =
    typeof data?.subscription_id === "string" ? data.subscription_id : null
  const customerId =
    typeof data?.customer_id === "string" ? data.customer_id : null
  const sessionId =
    typeof data?.session_id === "string" ? data.session_id : null
  const url = typeof data?.url === "string" ? data.url : null
  const clientSecret =
    typeof data?.client_secret === "string" ? data.client_secret : null
  const summary =
    data?.summary && typeof data.summary === "object" ? data.summary : undefined

  if (normalizedFlow === "billing_portal") {
    if (!url) {
      const error = new Error(
        "Stripe did not return a billing portal link for this organization.",
      )
      Object.assign(error, { payload: data })
      throw error
    }
    return {
      flow: "billing_portal",
      url,
      subscriptionId,
      customerId,
      plan: planMetadata,
    }
  }

  if (normalizedFlow === "embedded_checkout") {
    if (!clientSecret || !sessionId) {
      const error = new Error(
        "Stripe did not return an embedded Checkout session.",
      )
      Object.assign(error, { payload: data })
      throw error
    }
    return {
      flow: "embedded_checkout",
      purpose: data?.purpose || resolvedPurpose,
      clientSecret,
      sessionId,
      customerId,
      summary,
    }
  }

  const error = new Error("Unsupported billing flow returned by server.")
  Object.assign(error, { payload: data })
  throw error
}

export async function getOrganizationBillingSummary(organizationId) {
  if (!organizationId) return null
  const { data, error } = await supabase.rpc(
    "get_organization_billing_summary",
    { p_organization_id: organizationId },
  )
  if (error) throw error
  return Array.isArray(data) ? data[0] ?? null : data
}

async function postBillingFunction(path, organizationId) {
  if (!organizationId) throw new Error("organizationId is required")
  if (!FUNCTIONS_URL) throw new Error("Supabase function URL not configured")
  const token = await getValidSupabaseAccessToken(supabase)
  if (!token) throw new Error("Missing access token")
  const response = await fetch(`${FUNCTIONS_URL}/${path}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ organization_id: organizationId }),
  })
  const data = await response.json().catch(() => ({}))
  if (!response.ok) {
    const error = new Error(data?.error || `Billing request failed: ${path}`)
    Object.assign(error, { status: response.status, payload: data })
    throw error
  }
  return data
}

export const expireStripeCheckout = (organizationId) =>
  postBillingFunction("expire-checkout-session", organizationId)

export const getResumePaymentIntent = (organizationId) =>
  postBillingFunction("get-resume-payment-intent", organizationId)

export async function syncStripeSubscription(organizationId) {
  if (!organizationId) return { status: "skipped", reason: "missing_org" }
  if (!FUNCTIONS_URL) {
    console.warn("Supabase function URL not configured; skipping Stripe sync")
    return { status: "skipped", reason: "missing_function_url" }
  }

  const token = await getValidSupabaseAccessToken(supabase)
  if (!token) {
    console.warn("Skipping Stripe sync because no access token is available")
    return { status: "skipped", reason: "missing_token" }
  }

  const response = await fetch(`${FUNCTIONS_URL}/sync-subscription-quantity`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ organization_id: organizationId }),
  })

  const data = await response.json().catch(() => ({}))
  if (!response.ok) {
    const errorMessage = data?.error || "Failed to sync Stripe subscription"
    throw new Error(errorMessage)
  }

  return data
}

export async function cancelStripeSubscription(organizationId) {
  if (!organizationId) throw new Error("organizationId is required")
  if (!FUNCTIONS_URL) {
    throw new Error("Supabase function URL not configured")
  }

  const token = await getValidSupabaseAccessToken(supabase)
  if (!token) {
    throw new Error("Missing access token")
  }

  const response = await fetch(`${FUNCTIONS_URL}/cancel-subscription`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ organization_id: organizationId }),
  })

  const data = await response.json().catch(() => ({}))
  if (!response.ok) {
    const errorMessage = data?.error || "Failed to cancel Stripe subscription"
    const error = new Error(errorMessage)
    Object.assign(error, {
      status: response.status,
      payload: data,
    })
    throw error
  }

  return data
}
